import React from 'react'
import { XStack, Text, Icon, AnimatePresence } from '@red-ui/components'
import { getString } from '@vrw/data'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { TextButton } from '../TextButton'
import { getCabinDetails } from '../../api/cabinDetails'
import { Alert } from '../Alert'

const CABIN_DETAILS_PROPERTIES_TO_SHOW = [
  {
    icon: <Icon.Armchair />,
    name: 'seatSelection',
  },
  {
    icon: <Icon.CodeSquare />,
    name: 'seatPitch',
  },
  {
    icon: <Icon.BaggageClaim />,
    name: 'checkedBags',
  },
  {
    icon: <Icon.Backpack />,
    name: 'carryOnBags',
  },
  {
    icon: <Icon.Star />,
    name: 'boardingPriority',
  },
  {
    icon: <Icon.CheckCircle />,
    name: 'checkInPriority',
  },
  {
    icon: <Icon.RockingChair />,
    name: 'loungeAccess',
  },
  {
    icon: <Icon.Apple />,
    name: 'freshFoods',
  },
  {
    icon: <Icon.CupSoda />,
    name: 'beverages',
  },
]

type Props = {
  id: string | null
  expanded: boolean
  onExpand: (isOpen: boolean) => void
}

export const SeatTypeInfo = ({ id, expanded, onExpand }: Props) => {
  const { data, error } = useCabinDetails(id)

  if (error) {
    return (
      <Alert
        boxStyling={{ backgroundColor: '$pink10', maxWidth: '100%' }}
        title={getString('redSkySearch.results.error.title')}
        body={error.message}
        icon={<Icon.AlertCircle color="$pink10" fill="#B3261E" alignSelf="center" minWidth="$3" />}
      />
    )
  }

  if (
    !data?.brandBFF.cabinDetails.legs.length ||
    // we don't show non-direct flights in the results (direct flights consist one leg only)
    data?.brandBFF.cabinDetails.legs.length > 1
  ) {
    return null
  }

  const cabinDetailsData = data?.brandBFF.cabinDetails.legs[0]

  const detailsItems = CABIN_DETAILS_PROPERTIES_TO_SHOW.reduce<JSX.Element[]>((acc, property) => {
    const propertyValue = cabinDetailsData[property.name as keyof typeof cabinDetailsData]
    if (!propertyValue) return acc

    acc.push(
      <XStack key={property.name} marginBottom="$2" alignItems="center">
        {property.icon}
        <Text fontSize="$3" $gtTabletLandscape={{ fontSize: '$4' }} marginLeft="$2">
          {propertyValue}
        </Text>
      </XStack>
    )
    return acc
  }, [])

  const trimmedDetailsItems = expanded ? detailsItems : detailsItems.slice(0, 3)

  return (
    <>
      <Text fontWeight="600" fontSize="$4" $gtMobile={{ fontSize: '$7' }} marginBottom="$3">
        {cabinDetailsData.dominantCabinName}
      </Text>
      <AnimatePresence>{trimmedDetailsItems}</AnimatePresence>
      {detailsItems.length > 3 && (
        <XStack>
          <TextButton onPress={() => onExpand(!expanded)} $gtMobile={{ fontSize: '$5' }} $gtTabletPortrait={{ fontSize: '$6' }}>
            {getString(`redSkySearch.results.card.seat.info.${expanded ? 'less' : 'more'}`)}
          </TextButton>
        </XStack>
      )}
    </>
  )
}

export const CABIN_DETAILS_QUERY_KEY = 'cabin-details'

function useCabinDetails(id: string | null) {
  const [searchParams] = useSearchParams()
  const passengersQueryStr = searchParams.get('passengers')

  return useQuery({
    queryKey: [CABIN_DETAILS_QUERY_KEY, id, passengersQueryStr],
    enabled: !!id && !!passengersQueryStr,
    queryFn: () => {
      if (!id || !passengersQueryStr) return null
      return getCabinDetails(id, passengersQueryStr)
    },
  })
}
